import type { ComponentType } from "react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { CaseClaimFragmentFragment } from "@alaffia-technology-solutions/client-sdk";
import type {
  AnnotationCoords,
  FileWithCitations,
} from "@alaffia-technology-solutions/components";
import type { Agent, AgentMessage } from "@alaffia-technology-solutions/schema";
import type { Awaitable } from "@alaffia-technology-solutions/types";

import { AgentMessageUserValidationStatus } from "@alaffia-technology-solutions/client-sdk";
import {
  apiClient,
  useAlaffiaClientContext,
} from "@alaffia-technology-solutions/client-sdk-react-query";
import {
  A,
  AgentLi,
  CitationsSources,
  List,
  Markdown,
  MarkdownContextProvider,
  TaskList,
  useResizablePanel,
} from "@alaffia-technology-solutions/components";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Separator,
} from "@alaffia-technology-solutions/ds";
import { useKeyPressEvent } from "@alaffia-technology-solutions/hooks";

import type { Operation as JsonPatchOperation } from "fast-json-patch";

import { applyPatch } from "fast-json-patch";
import { FileText, HandHelping } from "lucide-react";

import type { AgentStateSchema } from "../cases/hooks/useCaseWorkflowDialog";
import type { DeepLinkData, FilePreviewDeepLinkData } from "../utils";

import {
  createDeepLinkDataFromInlineCitations,
  createHref,
  getFilePreviewDeepLinkTargets,
} from "../utils";
import { AgentView } from "./AgentView";
import { ClaimDataCard } from "./ClaimDataCard";
import { useParsedTaskArtifactOutput } from "./hooks";
import { groupCitationsByFileId } from "./utils";

interface UseAgentTabProps {
  agentId: Agent["id"];
  agentState: AgentStateSchema | null;
  onAgentStateChange: (newState: AgentStateSchema) => Awaitable<void>;
}

const agentMessageUserValidationMap = {
  [AgentMessageUserValidationStatus.ACCEPTED]: "accepted",
  [AgentMessageUserValidationStatus.UNSET]: null,
} as const;

const useAgentTab = ({
  agentState,
  agentId,
  onAgentStateChange,
}: UseAgentTabProps) => {
  const [pendingPatchOperations, setPendingPatchOperations] = useState<
    JsonPatchOperation[]
  >([]);

  const editMode = agentState?.editMode ?? false;

  const { data, isLoading } = apiClient.cases.getAgent.useQuery(
    { id: agentId },
    {
      refetchOnMount: !editMode,
      refetchOnReconnect: !editMode,
      refetchOnWindowFocus: !editMode,
    },
  );

  const agentRecommendation = useMemo(() => data?.recommendation, [data]);

  const getAgentCtx = apiClient.cases.getAgent.useQueryContext();

  const { mutate: createAgentMessageRevision, isPending: isSubmittingEdit } =
    apiClient.cases.createAgentMessageRevision.useMutation({
      onSuccess: async () => {
        await updateAgentState({ editMode: false });
        setPendingPatchOperations([]);
        return getAgentCtx.invalidate({ id: agentId });
      },
    });

  const agentMessages = useMemo(
    () => data?.messages ?? [],
    [data],
  ) satisfies AgentMessage[];

  const agentMessage = useMemo(
    () => agentMessages.find((msg) => msg.id === agentState?.messageId) ?? null,
    [agentMessages, agentState?.messageId],
  );

  const updateAgentState = useCallback(
    (agentStateUpdate: Partial<AgentStateSchema>) => {
      if (!agentState) {
        return;
      }
      return onAgentStateChange({
        ...agentState,
        ...agentStateUpdate,
      });
    },
    [agentState, onAgentStateChange],
  );

  const changeAgentMessage = useCallback(
    (agentMessageId: AgentMessage["id"] | undefined) => {
      // Cannot change selected agent message when edit mode is enabled
      if (editMode || !agentMessageId) {
        return;
      }

      return updateAgentState({
        messageId: agentMessageId,
      });
    },
    [editMode, updateAgentState],
  );

  const agentMessagesList = useMemo(
    () =>
      agentMessages.map((item) => {
        const validationStatus = item.userValidation?.status
          ? agentMessageUserValidationMap[item.userValidation.status]
          : null;

        return {
          id: item.id,
          isActive: item.id === agentState?.messageId,
          name: item?.name,
          validationStatus,
          onClick: () => changeAgentMessage(item.id),
          disabled: editMode,
        };
      }),
    [agentMessages, agentState?.messageId, changeAgentMessage, editMode],
  );

  const currentIndex = agentMessages.findIndex(
    (message) => message.id === agentState?.messageId,
  );
  const hasNext = currentIndex < agentMessages.length - 1;
  const hasPrevious = currentIndex > 0;

  const onNext = () => changeAgentMessage(agentMessages[currentIndex + 1]?.id);

  const onPrevious = () =>
    changeAgentMessage(agentMessages[currentIndex - 1]?.id);

  const startEdit = () => {
    setPendingPatchOperations([]);
    return updateAgentState({
      editMode: true,
    });
  };

  const updatePendingEdit = (patchOperations: JsonPatchOperation[]) =>
    setPendingPatchOperations(patchOperations);

  const cancelEdit = () => {
    setPendingPatchOperations([]);
    return updateAgentState({
      editMode: false,
    });
  };

  const isEdited = pendingPatchOperations.length > 0;

  const submitEdit = (data: { reason?: string }) => {
    if (!agentMessage) {
      return;
    }
    const currentTaskArtifactOutput = agentMessage.content
      .taskArtifactOutput as unknown;
    const { newDocument: revisedTaskArtifactOutput } = applyPatch(
      currentTaskArtifactOutput,
      pendingPatchOperations,
      false,
      false,
    );
    createAgentMessageRevision({
      agentMessageId: agentMessage.id,
      input: {
        reason: data.reason,
        revisedTaskArtifactOutput,
        revisionId: agentMessage.revision?.revisionId,
        revisionJsonPatches: pendingPatchOperations,
      },
    });
  };

  useEffect(() => {
    if (agentMessages[0] && !agentState?.messageId) {
      void onAgentStateChange({
        sidebarExpanded: true,
        editMode: false,
        messageId: agentMessages[0]?.id,
      });
    }
  }, [agentMessages, agentState?.messageId, onAgentStateChange]);

  useKeyPressEvent(
    "Escape",
    (e) => {
      if (!editMode) {
        return;
      }
      e.preventDefault();
      void cancelEdit();
    },
    { capture: true },
  );

  return {
    agentMessage,
    agentMessagesList,
    agentRecommendation,
    editMode,
    hasNext,
    hasPrevious,
    isEdited,
    isLoading,
    isSubmittingEdit,
    onNext,
    onPrevious,
    startEdit,
    updatePendingEdit,
    cancelEdit,
    submitEdit,
  };
};

export interface RenderAgentMarkdownProps {
  deepLinkData: DeepLinkData;
  markdown: string;
  onDeepLinkClick?: (data: FilePreviewDeepLinkData) => void;
}

export type RenderAgentRecommendationMarkdownProps = RenderAgentMarkdownProps;

type AgentTabProps = UseAgentTabProps & {
  onCitationClick: (props: {
    annotations?: AnnotationCoords[];
    fileId: string;
    page: number;
  }) => void;
  renderAgentRecommendationMarkdown?: ComponentType<RenderAgentMarkdownProps>;
  renderAgentMessageMarkdown?: ComponentType<RenderAgentMarkdownProps>;
  // undefined when the caller did not opt into fetching the linked claim
  // (feature flag off); null when the case has no linked claim.
  claim?: CaseClaimFragmentFragment["claim"] | null;
};

export function AgentTab({
  agentId,
  agentState,
  onAgentStateChange,
  onCitationClick,
  renderAgentMessageMarkdown: AgentMessageMarkdown,
  renderAgentRecommendationMarkdown: AgentRecommendationMarkdown,
  claim,
}: AgentTabProps) {
  const {
    agentMessage,
    agentMessagesList,
    agentRecommendation,
    editMode,
    hasNext,
    hasPrevious,
    isEdited,
    isLoading,
    isSubmittingEdit,
    onNext,
    onPrevious,
    updatePendingEdit,
    startEdit,
    cancelEdit,
    submitEdit,
  } = useAgentTab({
    agentId,
    agentState,
    onAgentStateChange,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);

  const { expandPanel, getResizablePanelProps, isExpanded, collapsePanel } =
    useResizablePanel({
      isExpanded: agentState?.sidebarExpanded ?? true,
      onIsExpandedChange: async (isExpanded) => {
        if (agentState) {
          await onAgentStateChange({
            ...agentState,
            sidebarExpanded: isExpanded,
          });
        }
      },
      wrapperRef,
    });

  const taskArtifactOutput = useParsedTaskArtifactOutput(
    agentMessage?.content.taskArtifactOutput,
    agentMessage?.schema,
  );

  const agentRecommendationCitations = useMemo(
    () =>
      (agentRecommendation?.content.citations ?? []).filter(
        (citation): citation is typeof citation & { page_index: number } =>
          citation.page_index != null,
      ),
    [agentRecommendation],
  );

  const { alaffiaClient } = useAlaffiaClientContext();

  const [citations, setCitations] = useState<FileWithCitations[]>([]);

  useEffect(() => {
    const fetchCitations = async () => {
      if (!agentRecommendationCitations.length) {
        setCitations([]);
        return;
      }

      const grouped = groupCitationsByFileId(agentRecommendationCitations);
      const citationPromises = Object.entries(grouped).map(
        async ([fileId, fileCitations]) => {
          try {
            const { data } = await alaffiaClient.files.getFile({ id: fileId });

            if (!data) {
              console.warn(`File with ID: ${fileId} not found.`);
            }

            return {
              fileId,
              fileName: data?.fileName ?? fileId,
              citations: fileCitations.map((c) => ({
                citationKey: c.citation_key,
                page: c.page_index,
              })),
            };
          } catch (error) {
            console.error(`Error fetching file ${fileId}:`, error);
            return null;
          }
        },
      );

      const resolvedCitations = await Promise.all(citationPromises);
      setCitations(resolvedCitations.filter(Boolean) as FileWithCitations[]);
    };

    void fetchCitations();
  }, [agentRecommendationCitations, alaffiaClient]);

  const deepLinkData = useMemo(
    () =>
      createDeepLinkDataFromInlineCitations(
        agentRecommendation?.citations ?? [],
      ),
    [agentRecommendation?.citations],
  );

  const handleDeepLinkClick = useCallback(
    ({ annotations, fileId, page }: FilePreviewDeepLinkData) => {
      onCitationClick({ annotations: annotations ?? [], fileId, page });
    },
    [onCitationClick],
  );

  const recommendationA = useCallback(
    (aProps: React.ComponentPropsWithoutRef<"a">) => (
      <A
        deepLinkData={deepLinkData}
        renderDeepLink={({ children }, deepLink) => {
          const data = deepLinkData[deepLink.host]?.[deepLink.paths[0]];
          if (!data) {
            return (
              <a className="af-text-primary af-underline" {...aProps}>
                {children}
              </a>
            );
          }
          const targets = getFilePreviewDeepLinkTargets(data);
          return (
            <span className="af-inline-flex af-items-center af-gap-1">
              {targets.map((target, index) => (
                <a
                  key={`${target.fileId}-${target.page}-${index}`}
                  title={`Open source ${index + 1} of ${targets.length}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeepLinkClick(target);
                  }}
                  className="af-rounded-[6px] af-border af-border-transparent af-bg-input af-px-1 af-text-sm af-font-bold af-text-interface-foreground af-no-underline"
                  href={createHref(window.location.pathname, {
                    "file-preview-file-id": target.fileId,
                    "file-preview-page": target.page.toString(),
                    ...(target.annotations && {
                      "file-preview-annotations": JSON.stringify(
                        target.annotations,
                      ),
                    }),
                  })}
                >
                  {children}
                  {targets.length > 1 && <sup>{index + 1}</sup>}
                </a>
              ))}
            </span>
          );
        }}
        {...aProps}
      />
    ),
    [deepLinkData, handleDeepLinkClick],
  );

  return (
    <div className="af-h-full af-overflow-hidden" ref={wrapperRef}>
      {isLoading ? null : (
        <ResizablePanelGroup direction="horizontal">
          <ResizablePanel
            minSize={30}
            maxSize={50}
            {...getResizablePanelProps()}
          >
            <TaskList
              isExpanded={isExpanded}
              onCollapse={collapsePanel}
              onExpand={expandPanel}
            >
              {agentRecommendation && (
                <List.Item
                  className="af-h-8 af-px-2 af-py-1.5 af-text-sm af-text-black"
                  isActive={agentState?.messageId === "recommendation"}
                  onClick={async () => {
                    if (agentState) {
                      await onAgentStateChange({
                        ...agentState,
                        messageId: "recommendation",
                      });
                    }
                  }}
                >
                  <HandHelping className="af-size-4" />
                  {isExpanded ? "Recommendations" : null}
                </List.Item>
              )}
              {agentMessagesList.length > 0 && (
                <List.Item
                  className="af-pointer-events-none af-mt-2 af-h-8 af-px-2 af-py-1.5 af-font-medium af-text-interface-foreground/70"
                  disabled={true}
                  isActive={false}
                >
                  {isExpanded ? "Tasks" : null}
                </List.Item>
              )}
              {agentMessagesList.map(({ id, ...item }) => (
                <TaskList.Item key={id} {...item} isExpanded={isExpanded} />
              ))}
              {claim !== undefined && agentMessagesList.length > 0 && (
                <>
                  <List.Item
                    className="af-pointer-events-none af-mt-2 af-h-8 af-px-2 af-py-1.5 af-font-medium af-text-interface-foreground/70"
                    disabled={true}
                    isActive={false}
                  >
                    {isExpanded ? "Additional Information" : null}
                  </List.Item>
                  <List.Item
                    className="af-h-8 af-px-2 af-py-1.5 af-text-sm af-text-black"
                    isActive={agentState?.messageId === "claim-data"}
                    onClick={async () => {
                      if (agentState) {
                        await onAgentStateChange({
                          ...agentState,
                          messageId: "claim-data",
                        });
                      }
                    }}
                  >
                    <FileText className="af-size-4" />
                    {isExpanded ? "Claim Data" : null}
                  </List.Item>
                </>
              )}
            </TaskList>
          </ResizablePanel>
          <ResizableHandle withHandle={false} />
          <ResizablePanel minSize={50} defaultSize={70}>
            {agentState?.messageId === "claim-data" && (
              <ClaimDataCard claim={claim ?? null} />
            )}
            {!agentMessage && agentState?.messageId !== "claim-data" && (
              <div className="af-flex af-max-h-full af-flex-col af-gap-2 af-p-4">
                <div className="af-flex af-h-[36px] af-items-center">
                  <h2 className="af-font-medium">Recommendations</h2>
                </div>
                <div className="af-border-ds-interface-border af-flex af-flex-1 af-flex-col af-gap-4 af-overflow-auto af-rounded-2xl af-border af-px-6 af-py-5">
                  <div className="af-flex af-flex-col af-gap-2">
                    {agentRecommendation?.content ? (
                      AgentRecommendationMarkdown ? (
                        <AgentRecommendationMarkdown
                          deepLinkData={deepLinkData}
                          markdown={agentRecommendation.content.markdown}
                          onDeepLinkClick={handleDeepLinkClick}
                        />
                      ) : (
                        <MarkdownContextProvider
                          value={{
                            components: { a: recommendationA, li: AgentLi },
                          }}
                        >
                          <Markdown>
                            {agentRecommendation.content.markdown}
                          </Markdown>
                        </MarkdownContextProvider>
                      )
                    ) : null}
                  </div>
                  {agentRecommendationCitations.length > 0 && (
                    <>
                      <Separator className="af-bg-ds-interface-border" />
                      <CitationsSources
                        citations={citations}
                        onFilePreviewCallback={({ fileId, page }) => {
                          if (!page) {
                            return;
                          }

                          onCitationClick({
                            fileId,
                            page,
                          });
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
            {agentMessage && (
              <AgentView>
                <AgentView.Body editMode={editMode}>
                  <AgentView.Header
                    markdownTitle={agentMessage.content.markdownTitle}
                  >
                    <AgentView.UserValidationStatus
                      userValidation={agentMessage.userValidation}
                    />
                    <AgentView.EditIndicator revision={agentMessage.revision} />
                  </AgentView.Header>
                  <AgentView.MessageContent
                    taskArtifactOutput={taskArtifactOutput}
                    editMode={editMode}
                    onCitationClick={onCitationClick}
                    onDeepLinkClick={handleDeepLinkClick}
                    onEdit={updatePendingEdit}
                    fallbackMarkdownContent={agentMessage.content.markdownBody}
                    renderMarkdown={AgentMessageMarkdown}
                  />
                </AgentView.Body>
                <AgentView.Footer>
                  {editMode ? (
                    <AgentView.EditReasonForm
                      onSubmit={submitEdit}
                      onCancel={cancelEdit}
                      defaultReason={agentMessage.revision?.reason}
                      isEdited={isEdited}
                      isLoading={isSubmittingEdit}
                    >
                      {agentMessage.userValidation?.status ===
                        AgentMessageUserValidationStatus.ACCEPTED && (
                        <AgentView.StatusChangeAlert />
                      )}
                    </AgentView.EditReasonForm>
                  ) : (
                    <>
                      <AgentView.PreviousButton
                        onClick={onPrevious}
                        disabled={!hasPrevious}
                      />
                      <AgentView.NextButton
                        aria-label="Next task"
                        onClick={onNext}
                        disabled={!hasNext}
                      />
                      {taskArtifactOutput?.markdown.editable && (
                        <AgentView.EditButton onClick={startEdit} />
                      )}
                      <AgentView.UserValidationButton
                        agentId={agentId}
                        agentMessage={agentMessage}
                      />
                    </>
                  )}
                </AgentView.Footer>
              </AgentView>
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );
}
